import { ResponsivePie } from "@nivo/pie";
import React from "react";
import colours from "styles/colours";

import { FixedSizeContainer, DisplayText, BoldedText } from "./style";

export interface ChartScheme {
  percentageColor: string;
  missingColor: string;
  redText: boolean;
}

interface Props {
  percentage: number;
  displayText: string;
  displayTextLabel?: string;
  className?: string;
  overHalfSchemeOverride?: Partial<ChartScheme>;
  defaultSchemeOverride?: Partial<ChartScheme>;
  missingSchemeOverride?: Partial<ChartScheme>;
}
/**
 * A hollow pie chart that visually represents a percentage with color mappings based on thresholds.
 * The central gap displays textual information about the percentage.
 *
 * **Props:**
 * - `percentage`: The percentage value represented by the filled portion of the pie chart.
 * - `displayText`: The text displayed inside the hollow center.
 * - `displayTextLabel` (optional): A label above `displayText` to provide additional context.
 *
 * **Color Thresholds:**
 * - `>50%` → Faded orange background with orange fill
 * - `<=50%` → Faded red background with red fill
 * - `0%` → Faded red background, and red text.
 */
const PercentageDonutChart: React.FC<Props> = ({
  percentage,
  displayText,
  displayTextLabel,
  className,
  overHalfSchemeOverride,
  defaultSchemeOverride,
  missingSchemeOverride,
}) => {
  const missingChartScheme: ChartScheme = {
    percentageColor: colours.crimsonRed,
    missingColor: colours.fadedRed,
    redText: true,
  };

  const defaultChartScheme: ChartScheme = {
    percentageColor: colours.crimsonRed,
    missingColor: colours.fadedRed,
    redText: false,
  };

  const overHalfChartScheme: ChartScheme = {
    percentageColor: colours.darkOrange,
    missingColor: colours.fadedOrange,
    redText: false,
  };

  const resolvedOverHalfScheme: ChartScheme = {
    ...overHalfChartScheme,
    ...(overHalfSchemeOverride ?? {}),
  };

  const resolvedDefaultScheme: ChartScheme = {
    ...defaultChartScheme,
    ...(defaultSchemeOverride ?? {}),
  };

  const resolvedMissingScheme: ChartScheme = {
    ...missingChartScheme,
    ...(missingSchemeOverride ?? {}),
  };

  const chartColorScheme =
    percentage > 50
      ? resolvedOverHalfScheme
      : percentage > 0
      ? resolvedDefaultScheme
      : resolvedMissingScheme;

  const chartData = [
    {
      id: "percentage",
      value: percentage,
    },
    {
      id: "missingPercentage",
      value: 100 - percentage,
    },
  ];

  const percentageColor = chartColorScheme.percentageColor;
  const missingColor = chartColorScheme.missingColor;

  return (
    <FixedSizeContainer
      className={className}
      data-test-id="percentage-donut-chart"
    >
      <DisplayText isRed={chartColorScheme.redText}>
        <span>
          {displayTextLabel && (
            <>
              {displayTextLabel}
              <br />
            </>
          )}
          <BoldedText>{displayText}</BoldedText>
        </span>
      </DisplayText>
      {/* The pie chart is not accessible (e.g. has role="img" but no way of adding an accessible
      title). Since the chart adds no information for screenreader users we hide it completely. */}
      <FixedSizeContainer aria-hidden>
        <ResponsivePie
          data={chartData}
          innerRadius={0.7}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          isInteractive={false}
          colors={[percentageColor, missingColor]}
        />
      </FixedSizeContainer>
    </FixedSizeContainer>
  );
};

export default PercentageDonutChart;
